import {
  EarningMechanism as EarningMechanismAPI,
  ExternalFulfillmentEM as ExternalFulfillmentAPI,
  ExternalPointAward as ExternalPointAwardAPI,
  LoyaltyEventEM as LoyaltyEventAPI,
  MembershipEventEM as MembershipEventAPI,
  NullAward as NullAwardAPI,
  PerGroupPurchasedEM as PerGroupPurchasedAPI,
  PerItemPurchasedEM as PerItemPurchasedAPI,
  PerItemsAccumulatedEM as PerItemsAccumulatedAPI,
  PerTaggedGroupPurchasedEM as PerTaggedGroupPurchasedAPI,
  PerTaggedItemPurchasedEM as PerTaggedItemPurchasedAPI,
  PerTaggedItemsAccumulatedEM as PerTaggedItemsAccumulatedAPI,
  PointAward as PointAwardAPI,
  PointsPerAward as PointsPerAwardAPI,
  ProductPurchaseEM as ProductPurchaseAPI,
  TotalEligiblePurchaseEM as TotalEligiblePurchaseAPI,
  TotalEligiblePurchaseOverTimeEM as TotalEligiblePurchaseOverTimeAPI,
  TotalSpendOnTransactionEM as TotalSpendOnTransactionAPI,
  TotalSpendOverTimeEM as TotalSpendOverTimeAPI,
  VarietyPurchaseEM as VarietyPurchaseAPI,
  VarietyPurchaseOverTimeEM as VarietyPurchaseOverTimeAPI,
  VisitsEM as VisitsAPI,
} from '@occc-app/Activate/apiTypes';

import { State, Awards, EarningMechanisms } from './types';

type DeserializeAward = (
  awardBody: ExternalPointAwardAPI | NullAwardAPI | PointAwardAPI | PointsPerAwardAPI,
) => Awards;
const deserializeAward: DeserializeAward = (awardBody) => {
  switch (awardBody.award_type) {
    case 'external_point_award':
      return {
        type: 'externalPointAward',
        maximum: awardBody.configuration.maximum,
      };
    case 'null':
      return {
        type: 'nullAward',
      };
    case 'point_award':
      return {
        type: 'pointAward',
        points: awardBody.configuration.points,
      };
    case 'points_per':
      return {
        type: 'pointsPerAward',
        points: awardBody.configuration.points,
        pointRounding: awardBody.configuration.rounding,
      };
  }
};

type DeserializeExternalFulfillment = (
  configurationBody: ExternalFulfillmentAPI['configuration'],
) => State['emConfiguration']['externalFulfillment'];
const deserializeExternalFulfillment: DeserializeExternalFulfillment = () => ({
  type: 'externalFulfillment',
});

type DeserializeLoyaltyEvent = (
  configurationBody: LoyaltyEventAPI['configuration'],
) => State['emConfiguration']['loyaltyEvent'];
const deserializeLoyaltyEvent: DeserializeLoyaltyEvent = (configurationBody) => ({
  type: 'loyaltyEvent',
  eventType: configurationBody.event_type,
});

type DeserializeMembershipEvent = (
  configurationBody: MembershipEventAPI['configuration'],
) => State['emConfiguration']['membershipEvent'];
const deserializeMembershipEvent: DeserializeMembershipEvent = (configurationBody) => ({
  type: 'membershipEvent',
  eventType: configurationBody.event_type,
});

type DeserializePerGroupPurchased = (
  configurationBody: PerGroupPurchasedAPI['configuration'],
) => State['emConfiguration']['perGroupPurchased'];
const deserializePerGroupPurchased: DeserializePerGroupPurchased = (configurationBody) => ({
  type: 'perGroupPurchased',
  groups: configurationBody.groups.map((group, i) => ({
    countPer: group.count_per,
    countThreshold: group.count_threshold,
    dirty: false,
    groupIndex: i,
    groupId: group.group_id,
    itemKey: group.key,
    removed: false,
    summationMode: group.summation_mode,
    values: group.values,
  })),
});

type DeserializePerItemPurchased = (
  configurationBody: PerItemPurchasedAPI['configuration'],
) => State['emConfiguration']['perItemPurchased'];
const deserializePerItemPurchased: DeserializePerItemPurchased = (configurationBody) => ({
  type: 'perItemPurchased',
  itemKey: configurationBody.key,
  countPer: configurationBody.count_per,
  countRounding: configurationBody.count_rounding,
  summationMode: configurationBody.summation_mode,
  values: configurationBody.values,
});

type DeserializePerItemsAccumulated = (
  configurationBody: PerItemsAccumulatedAPI['configuration'],
) => State['emConfiguration']['perItemsAccumulated'];
const deserializePerItemsAccumulated: DeserializePerItemsAccumulated = (configurationBody) => ({
  type: 'perItemsAccumulated',
  itemKey: configurationBody.key,
  countPer: configurationBody.count_per,
  countThreshold: configurationBody.count_threshold,
  summationMode: configurationBody.summation_mode,
  values: configurationBody.values,
});

type DeserializePerTaggedGroupPurchased = (
  configurationBody: PerTaggedGroupPurchasedAPI['configuration'],
) => State['emConfiguration']['perTaggedGroupPurchased'];
const deserializePerTaggedGroupPurchased: DeserializePerTaggedGroupPurchased = (
  configurationBody,
) => ({
  type: 'perTaggedGroupPurchased',
  groups: configurationBody.groups.map((group, i) => ({
    countPer: group.count_per,
    countThreshold: group.count_threshold,
    dirty: false,
    groupIndex: i,
    groupId: group.group_id,
    removed: false,
    summationMode: group.summation_mode,
    tags: group.tags,
  })),
});

type DeserializePerTaggedItemPurchased = (
  configurationBody: PerTaggedItemPurchasedAPI['configuration'],
) => State['emConfiguration']['perTaggedItemPurchased'];
const deserializePerTaggedItemPurchased: DeserializePerTaggedItemPurchased = (
  configurationBody,
) => ({
  type: 'perTaggedItemPurchased',
  countPer: configurationBody.count_per,
  countRounding: configurationBody.count_rounding,
  summationMode: configurationBody.summation_mode,
  tags: configurationBody.tags,
});

type DeserializePerTaggedItemsAccumulated = (
  configurationBody: PerTaggedItemsAccumulatedAPI['configuration'],
) => State['emConfiguration']['perTaggedItemsAccumulated'];
const deserializePerTaggedItemsAccumulated: DeserializePerTaggedItemsAccumulated = (
  configurationBody,
) => ({
  type: 'perTaggedItemsAccumulated',
  countPer: configurationBody.count_per,
  countThreshold: configurationBody.count_threshold,
  summationMode: configurationBody.summation_mode,
  tags: configurationBody.tags,
});

type DeserializeProductPurchase = (
  configurationBody: ProductPurchaseAPI['configuration'],
) => State['emConfiguration']['productPurchase'];
const deserializeProductPurchase: DeserializeProductPurchase = (configurationBody) => ({
  type: 'productPurchase',
  countProductsBy: configurationBody.count_products_by,
  eligibleProductGroups: configurationBody.eligible_product_groups,
  purchaseThreshold: configurationBody.purchase_threshold,
});

type DeserializeTotalEligiblePurchase = (
  configurationBody: TotalEligiblePurchaseAPI['configuration'],
) => State['emConfiguration']['totalEligiblePurchase'];
const deserializeTotalEligiblePurchase: DeserializeTotalEligiblePurchase = (configurationBody) => ({
  type: 'totalEligiblePurchase',
  countProductsBy: configurationBody.count_products_by,
  eligibleProductGroups: configurationBody.eligible_product_groups,
  roundCountedProducts: configurationBody.round_counted_products,
});

type DeserializeTotalEligiblePurchaseOverTime = (
  configurationBody: TotalEligiblePurchaseOverTimeAPI['configuration'],
) => State['emConfiguration']['totalEligiblePurchaseOverTime'];
const deserializeTotalEligiblePurchaseOverTime: DeserializeTotalEligiblePurchaseOverTime = (
  configurationBody,
) => ({
  type: 'totalEligiblePurchaseOverTime',
  accumulationThreshold: configurationBody.accumulation_threshold,
  countProductsBy: configurationBody.count_products_by,
  eligibleProductGroups: configurationBody.eligible_product_groups,
});

type DeserializeTotalSpendOnTransaction = (
  configurationBody: TotalSpendOnTransactionAPI['configuration'],
) => State['emConfiguration']['totalSpendOnTransaction'];
const deserializeTotalSpendOnTransaction: DeserializeTotalSpendOnTransaction = (
  configurationBody,
) => ({
  type: 'totalSpendOnTransaction',
  ineligibleProductGroups: configurationBody.ineligible_product_groups,
  roundMoneyAmount: configurationBody.round_money_amount,
});

type DeserializeTotalSpendOverTime = (
  configurationBody: TotalSpendOverTimeAPI['configuration'],
) => State['emConfiguration']['totalSpendOverTime'];
const deserializeTotalSpendOverTime: DeserializeTotalSpendOverTime = (configurationBody) => ({
  type: 'totalSpendOverTime',
  accumulationThreshold: configurationBody.accumulation_threshold,
  ineligibleProductGroups: configurationBody.ineligible_product_groups,
});

type DeserializeVarietyPurchase = (
  configurationBody: VarietyPurchaseAPI['configuration'],
) => State['emConfiguration']['varietyPurchase'];
const deserializeVarietyPurchase: DeserializeVarietyPurchase = (configurationBody) => ({
  type: 'varietyPurchase',
  products: configurationBody.products.map((product, i) => ({
    countProductsBy: product.count_products_by,
    eligibleProductGroups: product.eligible_product_groups,
    dirty: false,
    productId: product.product_id,
    productIndex: i,
    purchaseThreshold: product.purchase_threshold,
    removed: false,
  })),
});

type DeserializeVarietyPurchaseOverTime = (
  configurationBody: VarietyPurchaseOverTimeAPI['configuration'],
) => State['emConfiguration']['varietyPurchaseOverTime'];
const deserializeVarietyPurchaseOverTime: DeserializeVarietyPurchaseOverTime = (
  configurationBody,
) => ({
  type: 'varietyPurchaseOverTime',
  products: configurationBody.products.map((product, i) => ({
    countProductsBy: product.count_products_by,
    eligibleProductGroups: product.eligible_product_groups,
    dirty: false,
    productId: product.product_id,
    productIndex: i,
    purchaseThreshold: product.purchase_threshold,
    removed: false,
  })),
});

type DeserializeVisits = (
  configurationBody: VisitsAPI['configuration'],
) => State['emConfiguration']['visits'];
const deserializeVisits: DeserializeVisits = (configurationBody) => ({
  type: 'visits',
  visitThreshold: configurationBody.visit_threshold,
});

type Configurations =
  | ExternalFulfillmentAPI
  | LoyaltyEventAPI
  | MembershipEventAPI
  | PerGroupPurchasedAPI
  | PerItemPurchasedAPI
  | PerItemsAccumulatedAPI
  | PerTaggedGroupPurchasedAPI
  | PerTaggedItemPurchasedAPI
  | PerTaggedItemsAccumulatedAPI
  | ProductPurchaseAPI
  | TotalEligiblePurchaseAPI
  | TotalEligiblePurchaseOverTimeAPI
  | TotalSpendOnTransactionAPI
  | TotalSpendOverTimeAPI
  | VarietyPurchaseAPI
  | VarietyPurchaseOverTimeAPI
  | VisitsAPI;

type DeserializeConfiguration = (earningMechanismBody: Configurations) => EarningMechanisms;
const deserializeConfiguration: DeserializeConfiguration = (configurationBody) => {
  switch (configurationBody.earning_mechanism_type) {
    case 'external_fulfillment':
      return deserializeExternalFulfillment(configurationBody.configuration);
    case 'loyalty_event':
      return deserializeLoyaltyEvent(configurationBody.configuration);
    case 'membership_event':
      return deserializeMembershipEvent(configurationBody.configuration);
    case 'per_group_purchased':
      return deserializePerGroupPurchased(configurationBody.configuration);
    case 'per_item_purchased':
      return deserializePerItemPurchased(configurationBody.configuration);
    case 'per_items_accumulated':
      return deserializePerItemsAccumulated(configurationBody.configuration);
    case 'per_tagged_group_purchased':
      return deserializePerTaggedGroupPurchased(configurationBody.configuration);
    case 'per_tagged_item_purchased':
      return deserializePerTaggedItemPurchased(configurationBody.configuration);
    case 'per_tagged_items_accumulated':
      return deserializePerTaggedItemsAccumulated(configurationBody.configuration);
    case 'product_purchase':
      return deserializeProductPurchase(configurationBody.configuration);
    case 'total_eligible_purchase':
      return deserializeTotalEligiblePurchase(configurationBody.configuration);
    case 'total_eligible_purchase_over_time':
      return deserializeTotalEligiblePurchaseOverTime(configurationBody.configuration);
    case 'total_spend_on_transaction':
      return deserializeTotalSpendOnTransaction(configurationBody.configuration);
    case 'total_spend_over_time':
      return deserializeTotalSpendOverTime(configurationBody.configuration);
    case 'variety_purchase':
      return deserializeVarietyPurchase(configurationBody.configuration);
    case 'variety_purchase_over_time':
      return deserializeVarietyPurchaseOverTime(configurationBody.configuration);
    case 'visits':
      return deserializeVisits(configurationBody.configuration);
  }
};

type DeserializeEarningMechanism = (body: EarningMechanismAPI) => {
  selectedAwardConfiguration: Awards;
  selectedEMConfiguration: EarningMechanisms;
};
export const deserializeEarningMechanism: DeserializeEarningMechanism = (body) => ({
  selectedAwardConfiguration: deserializeAward(body.attributes.award),
  selectedEMConfiguration: deserializeConfiguration(body.attributes),
});
